import React from 'react';
import { Button } from '../ui/button';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { inr } from '../../lib/api';

/**
 * VendorProductsTab — dual-layout product list.
 *
 *  - **Mobile (<sm)**: vertical card stack. Each card shows the thumbnail, name,
 *    price, AND the Edit/Delete actions as full-width labelled buttons. Tables
 *    don't fit in 360px viewports — the action column was being pushed off the
 *    right edge with no horizontal scroll, which the user flagged.
 *  - **Desktop (≥sm)**: original table layout, unchanged.
 */
const ImagePreview = ({ src }) => (
  <img
    src={src}
    alt=""
    className="w-12 h-12 object-cover rounded bg-gray-100 border border-gray-200"
    onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
  />
);

const VendorProductsTab = ({ vendor, products, onAdd, onEdit, onDelete }) => (
  <>
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
      <h2 className="text-lg font-bold">Your Products</h2>
      {vendor.status === 'active' && (
        <Button onClick={onAdd} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="vendor-add-product-btn">
          <Plus className="w-4 h-4 mr-1" /> Add Product
        </Button>
      )}
    </div>
    {vendor.status !== 'active' && (
      <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 mb-3 text-sm">
        Your account is <b>{vendor.status}</b>. Once an admin activates you, you can add products.
      </div>
    )}

    {products.length === 0 ? (
      <div className="bg-white border border-gray-200 rounded-md p-10 text-center text-gray-500">No products yet.</div>
    ) : (
      <>
        {/* MOBILE: card stack */}
        <ul className="sm:hidden space-y-2" data-testid="vendor-products-mobile-list">
          {products.map((p) => (
            <li key={p.id} className="bg-white border border-gray-200 rounded-md p-3 flex gap-3" data-testid={`vendor-product-card-${p.id}`}>
              <ImagePreview src={p.image} />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm text-gray-900 line-clamp-2">{p.name}</p>
                <p className="text-sm text-[#2c54b2] font-semibold mt-0.5">{inr(p.price)} <span className="text-gray-500 font-normal">/ {p.unit}</span></p>
                <div className="flex gap-2 mt-2">
                  <button onClick={() => onEdit(p)} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs border border-[#2c54b2]/30 text-[#2c54b2] rounded hover:bg-blue-50" data-testid={`vendor-edit-${p.id}`}>
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => onDelete(p.id)} className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50" data-testid={`vendor-delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>

        {/* DESKTOP: original table */}
        <div className="hidden sm:block bg-white border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr>
              <th className="px-4 py-2 text-left">Image</th>
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Price</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} className="border-t border-gray-100">
                  <td className="px-4 py-2"><ImagePreview src={p.image} /></td>
                  <td className="px-4 py-2 font-medium">{p.name}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{inr(p.price)} / {p.unit}</td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => onEdit(p)} className="p-1.5 hover:bg-blue-50 rounded text-[#2c54b2]" title="Edit" data-testid={`vendor-edit-desk-${p.id}`}><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(p.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600 ml-1" title="Delete" data-testid={`vendor-delete-desk-${p.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    )}
  </>
);

export default VendorProductsTab;
